import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import actionIcon from '../assets/action-icon.svg'
import loadingIcon from '../assets/loading-icon.svg'
import errorIcon from '../assets/error-icon.svg'
import successIcon from '../assets/success-icon.svg'

import { cn } from '../../lib/utils'

enum ButtonVariant {
  DEFAULT = 'default',
  DESTRUCTIVE = 'destructive',
  OUTLINE = 'outline',
  SECONDARY = 'secondary',
  GHOST = 'ghost',
  LINK = 'link',
  ACTIVE = 'active',
  MAIN_OUTLINE = 'main_outline',
  MAIN_ACTION = 'main_action',
  SQUARE_DISABLED = 'square_disabled',
  SQUARE_ACTIVE = 'square_active',
  SQUARE_ERROR = 'square_error',
}

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1 md:gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none  [&_svg:not([class*='size-'])]:size-3 md:[&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-white shadow-xs hover:translate-y-[-2px] hover:shadow-md transition-all duration-150 ease-in-out focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground hover:translate-y-[-2px] dark:bg-input/30 dark:border-input dark:hover:bg-input/50  transition-all duration-150 ease-in-out',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
        active:
          'custom-square-active text-white h-auto md:h-[39px] rounded-full hover:translate-y-[-2px] transition-all duration-150 ease-in-out',
        main_outline:
          'border border-main text-main shadow-xs h-auto h-9 md:h-[39px] rounded-full hover:translate-y-[-2px] hover:shadow-md transition-all duration-150 ease-in-out',
        main_action:
          'bg-main text-main-foreground shadow-xs h-auto h-9 md:h-[39px] rounded-full hover:translate-y-[-2px] hover:shadow-md transition-all duration-150 ease-in-out',
        square_disabled: 'custom-square-disabled w-[44px] h-[44px] shrink-0 text-white',
        square_active:
          'custom-square-active w-[44px] h-[44px] shrink-0 text-white hover:translate-y-[-2px] transition-all duration-150 ease-in-out ',
        square_error: 'custom-square-error  w-[44px] h-[44px] shrink-0 text-white',
      },
      size: {
        default: 'h-min-7 md:h-min-0 md:h-9 px-2 py-1 md:px-4 md:py-2 has-[>svg]:px-3',
        sm: 'h-min-7 md:h-min-0 md:h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-min-7 md:h-min-0 md:h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

type State = 'action' | 'success' | 'error' | 'loading' | 'disabled'

const buttonActionIcon = (state: State) => {
  switch (state) {
    case 'action':
    case 'disabled':
      return <img src={actionIcon} alt="action" />
    case 'success':
      return <img src={successIcon} alt="success" />
    case 'error':
      return <img src={errorIcon} alt="error" />
    case 'loading':
      return <img src={loadingIcon} alt="loading" />
  }
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

function ButtonWithMobileStyle({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({ variant, size, className }),
        'bg-transparent text-default border border-card-border',
      )}
      {...props}
    />
  )
}

export { Button, buttonVariants, buttonActionIcon, ButtonVariant, ButtonWithMobileStyle }
